import React, { useEffect, useState } from 'react';
import { Box, Grid2 as Grid, Paper } from '@mui/material';
import qs from 'qs';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import Analytics from '../../Services/AnalyticsService';
import PageWrapper from '../../Components/Entities/PageWrapper';
import FilterSelect from '../../Components/Entities/Search/FilterSelect';
import InfinitePosts from '../../Components/Entities/Search/InfinitePosts';
import SearchResults from '../../Components/Entities/Search/SearchResults';
import LoadMoreButton from '../../Components/Entities/Search/LoadMoreButton';
import SearchBox from '../../Components/Entities/Search/SearchBox';
import Title from '../../Components/Entities/Search/Title';
import MetaTags from '../../Components/MetaTags';
import { PostCategories, PostTypes } from '../../Utils/Types';
import { getQueryParams, updateQueryParams } from '../../Utils/Url';
import usePostsInfiniteQuery from '../../Hooks/usePostsInfiniteQuery';

type FilterParamsType = {
  filter?: {
    q?: string;
    type?: string[];
    tags?: string[];
  };
  page?: number;
};

function Search({ space }: any) {
  const intl = useIntl();
  const location = useLocation();

  const filtersFromParams = getQueryParams(location);
  const [dataTableParams, setDataTableParams] = useState<FilterParamsType>(filtersFromParams);

  const searcheablePostTypes = [
    PostTypes.IDEA,
    PostCategories.COMMUNICATION,
    PostCategories.CONSULTATION,
  ].flat();

  const handleFilterChange = (name: any, value: any) => {
    let newDataTableParams = {
      ...dataTableParams,
      filter: {
        ...dataTableParams?.filter,
        [name]: value,
      },
    };

    // When updating a filter, we reset the page offset
    if (name !== 'page') {
      newDataTableParams = { ...newDataTableParams, page: 0 };
    }

    setDataTableParams(newDataTableParams);
    updateQueryParams(newDataTableParams);
  };
  const enabled = !!Object.values(dataTableParams.filter || {}).filter((e) => e).length;
  const queryKey = qs.stringify(dataTableParams);

  const { pages, postsCount, paging, fetchNextPage } = usePostsInfiniteQuery(queryKey, {
    enabled,
    space,
    types: dataTableParams?.filter?.type,
    extraParams: {
      filter: {
        q: dataTableParams?.filter?.q,
      },
    },
    tags: dataTableParams?.filter?.tags,
    limit: 10,
    sort: '-dates',
  });

  useEffect(() => {
    Analytics.track('Space', 'Search');
  }, []);

  return (
    <>
      <MetaTags
        title={intl.formatMessage(
          { id: 'META_TAGS.PAGES_TITLES.SEARCH' },
          { spaceName: space.name },
        )}
        description={space.description}
        pictureUrl={space.picture_to_share_url}
        noindex
      />

      <PageWrapper>
        <Box component={Paper} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <SearchBox value={dataTableParams?.filter?.q || ''} onChange={handleFilterChange} />
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FilterSelect
                options={searcheablePostTypes.map((item) => {
                  return {
                    id: item,
                    label: intl.formatMessage({
                      id: `POSTS.FILTER_TYPE_${item.toUpperCase()}`,
                    }),
                  };
                })}
                value={dataTableParams?.filter?.type}
                onChange={handleFilterChange}
                attribute="type"
                placeholder={intl.formatMessage({ id: 'SEARCH.TYPES_PLACEHOLDER' })}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FilterSelect
                options={space.included.tags}
                value={dataTableParams?.filter?.tags}
                attribute="tags"
                onChange={handleFilterChange}
                placeholder={intl.formatMessage({ id: 'SEARCH.CATEGORIES_PLACEHOLDER' })}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 7, mb: 4 }}>
          <Title paging={paging} query={dataTableParams?.filter?.q} />
        </Box>
        <SearchResults search={dataTableParams?.filter?.q} postsCount={postsCount}>
          <InfinitePosts posts={pages.flatMap((page: any) => page?.data?.data)} space={space} />
        </SearchResults>
        <Box>
          <LoadMoreButton paging={paging} onClick={fetchNextPage} />
        </Box>
      </PageWrapper>
    </>
  );
}

export default Search;
